import React, { useState } from 'react';
import { Box, Typography, Avatar, IconButton, Tooltip, Collapse, LinearProgress } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorIcon from '@mui/icons-material/Error';
import InstagramIcon from '@mui/icons-material/Instagram';

const STATUS_COLORS = { active: '#10b981', paused: '#f59e0b', completed: '#6366f1', cancelled: '#ef4444' };

const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const timeUntil = (date) => {
  const diff = new Date(date).getTime() - Date.now();
  if (diff <= 0) return 'now';
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hrs > 24) return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
};

const initial = (name) => (name || '?').charAt(0).toUpperCase();
const avatarColor = (name) => {
  const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

export default function CampaignCard({ campaign, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const totalSteps = campaign.sequenceId?.steps?.length || 0;
  const progress = totalSteps > 0 ? Math.min(campaign.currentStep / totalSteps, 1) : 0;
  const statusColor = STATUS_COLORS[campaign.status] || '#6b7280';

  return (
    <Box
      sx={{
        bgcolor: '#fff', borderRadius: 3, border: '1px solid #f0f0f0',
        transition: 'all 0.2s', cursor: 'pointer',
        '&:hover': { borderColor: '#d1d5db', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }} onClick={() => onSelect?.(campaign)}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: avatarColor(campaign.contactName), fontSize: 16, fontWeight: 700 }}>
          {initial(campaign.contactName)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{campaign.contactName}</Typography>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: statusColor, textTransform: 'capitalize' }}>{campaign.status}</Typography>
            {campaign.source === 'instagram' && (
              <InstagramIcon sx={{ fontSize: 14, color: '#e1306c' }} />
            )}
          </Box>
          <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.3 }}>{campaign.contactEmail}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            {campaign.status === 'active' && (
              <Tooltip title="Pause"><IconButton size="small" sx={{ width: 28, height: 28 }} onClick={(e) => { e.stopPropagation(); onSelect?.(campaign, 'pause'); }}><PauseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            )}
            {campaign.status === 'paused' && (
              <Tooltip title="Resume"><IconButton size="small" sx={{ width: 28, height: 28 }} onClick={(e) => { e.stopPropagation(); onSelect?.(campaign, 'resume'); }}><PlayArrowIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            )}
            {['active', 'paused'].includes(campaign.status) && (
              <Tooltip title="Cancel"><IconButton size="small" sx={{ width: 28, height: 28 }} onClick={(e) => { e.stopPropagation(); onSelect?.(campaign, 'cancel'); }}><CancelIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
            {campaign.sequenceId?.name || 'Unknown Sequence'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
            {Math.round(progress * 100)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress * 100}
          sx={{
            height: 6, borderRadius: 3, bgcolor: '#f3f4f6',
            '& .MuiLinearProgress-bar': { bgcolor: statusColor, borderRadius: 3 },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.8 }}>
          <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
            Step {campaign.currentStep}/{totalSteps || '?'}
          </Typography>
          {campaign.status === 'active' && campaign.nextSendAt ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleIcon sx={{ fontSize: 11, color: '#f59e0b' }} />
              <Typography sx={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>
                Next: {timeUntil(campaign.nextSendAt)}
              </Typography>
            </Box>
          ) : campaign.status === 'completed' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 11, color: '#6366f1' }} />
              <Typography sx={{ fontSize: 11, color: '#6366f1' }}>Done</Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      {campaign.sentEmails?.length > 0 && (
        <>
          <Box
            sx={{ px: 2.5, py: 1, borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setExpanded(p => !p)}
          >
            <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>
              {campaign.sentEmails.length} email{campaign.sentEmails.length > 1 ? 's' : ''} sent
            </Typography>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 16, color: '#9ca3af' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#9ca3af' }} />}
          </Box>
          <Collapse in={expanded}>
            <Box sx={{ px: 2.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {campaign.sentEmails.map((e, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  {e.error ? (
                    <ErrorIcon sx={{ fontSize: 14, color: '#ef4444', mt: 0.3 }} />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981', mt: 0.3 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>{e.subject}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                      {timeAgo(e.sentAt)}
                      {e.error ? <Typography component="span" sx={{ color: '#ef4444', ml: 1 }}>— {e.error}</Typography> : null}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
}
